import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, Button, Card } from 'reactstrap';
import { TranslationKey, useTranslation } from '../translations';
import { classNames } from '../utils';
import { MaterialIcon } from './MaterialIcon';

import './CardList.scss';

export interface CardListItemAction {
  readonly id: string;
  readonly text: TranslationKey;
  readonly to?: string;
  readonly action?: () => void;
  readonly externalUrl?: boolean;
  readonly color?: string;
  readonly outline?: boolean;
}

export interface CardListItem {
  readonly id: string;
  readonly title: React.JSX.Element | string;
  readonly subtitle: React.JSX.Element | string;
  readonly description?: React.JSX.Element | string;
  readonly shortenDescription?: boolean;
  readonly icon?: string;
  readonly badges?: React.JSX.Element;
  readonly actions: CardListItemAction[];
}

interface CardListProps {
  readonly children: React.JSX.Element[];
  readonly listName: string;
  readonly title?: string;
  readonly showCount?: boolean;
}

export const CardList: FC<CardListProps> = ({ children, listName, title, showCount }) => {
  return (
    <div className="card-list-wrapper container">
      {title && (
        <h4 className="card-list-title">
          {title}
          {showCount && (
            <Badge className="me-2" color={children.length === 0 ? 'secondary' : 'primary'}>
              {children.length}
            </Badge>
          )}
        </h4>
      )}
      <div className={classNames({}, 'card-list row row-cols-2', listName)}>{children}</div>
    </div>
  );
};

export const CardListItemElement: FC<CardListItem> = ({
  actions,
  badges,
  description,
  title,
  subtitle,
  icon,
}) => {
  return (
    <div className="col">
      <Card className="card-list-item">
        <div className="card-list-item-main">
          {icon && (
            <div className="card-list-item-icon">
              <MaterialIcon name={icon} />
            </div>
          )}
          <div className="card-list-item-details">
            <div className="card-list-item-title">{title}</div>
            <div className="card-list-item-subtitle">{subtitle}</div>
          </div>
          {actions.length > 0 && (
            <div className="card-list-item-actions">
              {actions.map((action) => (
                <CardListAction key={action.id} {...action} />
              ))}
            </div>
          )}
        </div>
        {badges && <div className="card-list-item-badges">{badges}</div>}
        {description && <div className="card-list-item-description">{description}</div>}
      </Card>
    </div>
  );
};

const CardListAction: FC<CardListItemAction> = ({
  text,
  to,
  id,
  action,
  externalUrl,
  color,
  outline,
}) => {
  const { ts } = useTranslation();
  const history = useHistory();
  const onClick = () => {
    if (action) {
      action();
    } else if (to) {
      if (externalUrl) {
        window.open(to, '_blank')?.focus();
      } else {
        history.push(to);
      }
    }
  };

  return (
    <Button
      className="card-list-item-action"
      color={color}
      id={`action-${id}`}
      onClick={onClick}
      outline={outline}
    >
      {ts(text)}
    </Button>
  );
};
